import { useState } from "react";
import { Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { getDeviceId } from "../../lib/storage";
import type { LmsComp } from "../../lib/lms";
import { TeamBadge } from "./TeamBadge";

export function PickPanel({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const me = comp.me!;
  const [busy, setBusy] = useState("");
  const pick = async (team: string) => {
    setBusy(team);
    try {
      const { data } = await api.post(`/lms/${comp.id}/pick`, { player_id: me.id, device_id: getDeviceId(), team });
      onChange(data);
      toast.success(`Locked in: ${team}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-white p-3 shadow-[0_4px_0_#0B1F33] sm:p-4" data-testid="lms-pick-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="ua-title text-2xl text-[#0E7A3E] sm:text-3xl">GW {comp.gameweek}: who wins?</h2>
        <p className="text-xs font-bold text-[#0B1F33]/70" data-testid="lms-my-pick">{me.pick ? <>Your pick: <strong>{me.pick}</strong> — tap another to change</> : "Pick one team. You can't use it again."}</p>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-5">
        {comp.teams.map((t) => {
          const used = me.used_teams.includes(t);
          const chosen = me.pick === t;
          return (
            <button key={t} disabled={used || !!busy} onClick={() => !chosen && pick(t)} data-testid={`lms-team-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`flex items-center gap-2 rounded-xl border-[2.5px] px-2 py-1.5 text-left text-xs font-extrabold transition-transform sm:text-sm ${chosen ? "border-[#0B1F33] bg-[#35E07A] shadow-[0_3px_0_#0B1F33]" : used ? "cursor-not-allowed border-dashed border-[#0B1F33]/30 bg-[#F1F1F1] text-[#0B1F33]/40" : "border-[#0B1F33] bg-[#F4FBF6] hover:-translate-y-0.5 hover:bg-[#E3F7EA]"}`}>
              <span className={used ? "opacity-40 grayscale" : ""}><TeamBadge team={t} size="sm" /></span>
              <span className="min-w-0 flex-1 truncate">{t}</span>
              {chosen && <Check className="h-4 w-4 shrink-0" strokeWidth={3} />}
              {used && <Lock className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />}
              {busy === t && <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-[#0B1F33] border-t-transparent" />}
            </button>
          );
        })}
      </div>
      <UsedTeams teams={me.used_teams} />
    </section>
  );
}

export function UsedTeams({ teams }: { teams: string[] }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs font-bold" data-testid="lms-used-teams">
      <span className="font-extrabold uppercase tracking-wide text-[#0B1F33]/60">Used:</span>
      {teams.length ? teams.map((t, i) => <span key={t} className="flex items-center gap-1 rounded-full border-2 border-[#0B1F33]/20 bg-[#F1F1F1] py-0.5 pl-0.5 pr-2"><TeamBadge team={t} size="sm" /> GW{i + 1}</span>) : <span className="text-[#0B1F33]/50">None yet — every team is available.</span>}
    </div>
  );
}
